import useMediaQuery from '@eversports/klimt-design-components/use-media-query'
import useTheme from '@eversports/klimt-design-components/use-theme'
import Box from '@eversports/klimt-primitives/legacy/Box/Box'
import { DialogContent, DialogTitle } from '@eversports/klimt-primitives/legacy/Dialog'
import Dialog from '@eversports/klimt-primitives/legacy/Dialog/Dialog'
import Stack from '@eversports/klimt-primitives/legacy/Stack/Stack'
import Text from '@eversports/klimt-primitives/legacy/Text/Text'
import { useMemo } from 'react'

import { Localized } from '../../../../localization/react'
import type { ScheduleSession, Ticket } from '../../ActivityDetails.types'
import Drawer from '../../components/Drawer'
import ScheduleCardContent from '../Schedule/ScheduleCardContent'
import ScheduleTable from '../Schedule/ScheduleTable'

import PriceLabel from './PriceLabel'
import TicketPriceText from './TicketPriceText'

const currentDate = new Date()

interface Props {
  ticket: Ticket
  sessions: Array<ScheduleSession>
  isOpen: boolean
  togglePopup: () => void
  isCorporateUser: boolean
  timeZone: string
}

const SessionsForTicketPopup = ({ ticket, sessions, isOpen, togglePopup, isCorporateUser, timeZone }: Props) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const upcomingSessions = useMemo(
    () => sessions.filter((session) => new Date(session.start) > currentDate),
    [sessions],
  )
  const pastSessions = useMemo(() => sessions.filter((session) => new Date(session.start) < currentDate), [sessions])

  const hasUpcomingSessions = upcomingSessions.length > 0
  const hasPastSessions = pastSessions.length > 0

  if (isMobile) {
    return (
      <Drawer isOpen={isOpen} handleClose={togglePopup}>
        <Box sx={{ paddingY: 5, background: 'white' }}>
          <Text variant="large" color="midnight" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            <Localized id="activity-detail-ticket-schedule-heading" />
          </Text>
        </Box>
        <Stack gap={5} sx={{ overflow: 'scroll' }}>
          <Stack
            gap={2}
            padding={3}
            sx={{ border: '1px solid', borderColor: 'mediumgray', backgroundColor: 'lightgray', borderRadius: 0.5 }}
          >
            <TicketPriceText ticket={ticket} />
            <Text>{ticket.name}</Text>
            <PriceLabel purchaseOption={ticket} isCorporateUser={isCorporateUser} />
          </Stack>
          {hasUpcomingSessions && (
            <Stack gap={1}>
              <Text color="midnight" sx={{ fontWeight: 'bold' }}>
                <Localized id="activity-detail-ticket-upcoming-sessions-label" />
              </Text>
              <Stack sx={{ 'div:last-child': { borderBottom: 'none' } }}>
                {upcomingSessions.map((session) => (
                  <ScheduleCardContent key={session.id} session={session} timeZone={timeZone} />
                ))}
              </Stack>
            </Stack>
          )}
          {hasPastSessions && (
            <Stack gap={1}>
              <Text color="midnight" sx={{ fontWeight: 'bold' }}>
                <Localized id="activity-detail-ticket-past-sessions-label" />
              </Text>
              <Stack sx={{ 'div:last-child': { borderBottom: 'none' } }}>
                {pastSessions.map((session) => (
                  <ScheduleCardContent key={session.id} session={session} timeZone={timeZone} />
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Drawer>
    )
  }

  return (
    <Dialog
      open={isOpen}
      onClose={togglePopup}
      sx={{
        '& .MuiDialog-paper': {
          background: 'white',
          border: '1px solid',
          borderColor: 'lightgray',
          boxSizing: 'border-box',
          boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.12)',
          borderRadius: '16px',
          minWidth: { xs: '90vw', lg: '800px' },
          maxHeight: '60vh',
        },
      }}
    >
      <DialogTitle>
        <Text variant="large" color="midnight" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          <Localized id="activity-detail-ticket-schedule-heading" />
        </Text>
      </DialogTitle>
      <DialogContent>
        <Stack gap={8}>
          {hasUpcomingSessions && (
            <Stack gap={3}>
              <Text color="midnight" sx={{ fontWeight: 'bold' }}>
                <Localized id="activity-detail-ticket-upcoming-sessions-label" />
              </Text>
              <ScheduleTable sessions={upcomingSessions} timeZone={timeZone} />
            </Stack>
          )}
          {hasPastSessions && (
            <Stack gap={3}>
              <Text color="midnight" sx={{ fontWeight: 'bold' }}>
                <Localized id="activity-detail-ticket-past-sessions-label" />
              </Text>
              <ScheduleTable sessions={pastSessions} timeZone={timeZone} />
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default SessionsForTicketPopup
